const API_BASE = import.meta.env.PROD ? 'https://zlandr-api.mike7081.workers.dev/api' : '/api';

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'An error occurred');
  }

  return data;
}

// Auth
export const auth = {
  requestMagicLink: (email: string) =>
    api<{ success: boolean; message: string }>('/auth/magic-link', {
      method: 'POST',
      body: { email },
    }),

  verifyToken: (token: string) =>
    api<{ success: boolean; user: { id: string; email: string } }>('/auth/verify', {
      method: 'POST',
      body: { token },
    }),

  getMe: () =>
    api<{ user: { id: string; email: string; stripe_customer_id: string | null } }>('/auth/me'),

  logout: () =>
    api<{ success: boolean }>('/auth/logout', { method: 'POST' }),
};

// Section Details Types
export interface FeatureItem {
  title: string;
  description: string;
}

export interface TestimonialItem {
  name: string;
  role: string;
  quote: string;
}

export interface PricingTier {
  name: string;
  price: string;
  period: string;
  features: string[];
  highlighted?: boolean;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface SectionDetails {
  hero?: {
    headline?: string;
    subheadline?: string;
    ctaText?: string;
    ctaUrl?: string;
  };
  features?: {
    sectionTitle?: string;
    items: FeatureItem[];
  };
  testimonials?: {
    sectionTitle?: string;
    items: TestimonialItem[];
  };
  pricing?: {
    sectionTitle?: string;
    tiers: PricingTier[];
  };
  faq?: {
    sectionTitle?: string;
    items: FAQItem[];
  };
  contact?: {
    sectionTitle?: string;
    subtitle?: string;
  };
}

// Generation
export interface GenerationInput {
  businessName: string;
  industry: string;
  description: string;
  targetAudience: string;
  primaryColor: string;
  secondaryColor: string;
  style: 'modern' | 'classic' | 'bold';
  sections: string[];
  contactEmail: string;
  websiteUrl?: string;
  logoUrl?: string;
  tagline?: string;
  sectionDetails?: SectionDetails;
  customCopy?: {
    headline?: string;
    subheadline?: string;
    ctaText?: string;
  };
}

export interface GenerationPreview {
  id: string;
  previewHtml: string;
}

export interface GenerationSession {
  sessionId: string;
  status: string;
  previews: GenerationPreview[];
  selectedVersion?: string;
  inputData: GenerationInput;
  expiresAt: string;
}

export const generate = {
  create: (input: GenerationInput) =>
    api<{ sessionId: string; previews: GenerationPreview[]; expiresAt: string }>('/generate', {
      method: 'POST',
      body: input,
    }),

  get: (sessionId: string) =>
    api<GenerationSession>(`/generate/${sessionId}`),

  select: (sessionId: string, version: string) =>
    api<{ success: boolean; selectedVersion: string }>(`/generate/${sessionId}/select`, {
      method: 'POST',
      body: { version },
    }),
};

// Payment
export const payment = {
  createSession: (sessionId: string, selectedVersion: string, plan = 'standard', customSubdomain?: string) =>
    api<{ checkoutUrl: string; paymentSessionId: string }>('/pay/create-session', {
      method: 'POST',
      body: { sessionId, selectedVersion, plan, customSubdomain },
    }),

  getStatus: (paymentSessionId: string) =>
    api<{ status: string; generationSessionId: string }>(`/pay/status/${paymentSessionId}`),
};

// Deployment
export const deploy = {
  trigger: (sessionId: string, stripeSessionId: string) =>
    api<{ success: boolean; siteId: string; subdomain: string; liveUrl: string }>('/deploy', {
      method: 'POST',
      body: { sessionId, stripeSessionId },
    }),

  getStatus: (siteId: string) =>
    api<{ status: string; subdomain: string; liveUrl: string; customDomain: string | null }>(`/deploy/${siteId}/status`),

  redeploy: (siteId: string) =>
    api<{ success: boolean; deploymentId: string; liveUrl: string }>(`/deploy/${siteId}/redeploy`, {
      method: 'POST',
    }),
};

// Sites
export interface Site {
  id: string;
  subdomain: string;
  customDomain: string | null;
  liveUrl: string;
  status: string;
  plan: string;
  subscriptionStatus: string;
  createdAt: string;
  settings: GenerationInput;
}

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string | null;
  created_at: string;
}

export const sites = {
  list: () =>
    api<{ sites: Site[] }>('/sites'),

  get: (siteId: string) =>
    api<{
      site: Site & { contactEmail: string; analyticsEnabled: boolean };
      leads: Lead[];
      analytics: { totalViews: number; totalLeads: number };
    }>(`/sites/${siteId}`),

  update: (siteId: string, data: Partial<{ contactEmail: string; analyticsEnabled: boolean; customSubdomain: string }>) =>
    api<{ success: boolean }>(`/sites/${siteId}`, {
      method: 'PUT',
      body: data,
    }),

  regenerate: (siteId: string, updatedSettings?: Partial<GenerationInput>) =>
    api<{ newSessionId: string; previews: GenerationPreview[]; siteId: string }>(`/sites/${siteId}/regenerate`, {
      method: 'POST',
      body: { updatedSettings },
    }),

  swap: (siteId: string, sessionId: string, selectedVersion: string) =>
    api<{ success: boolean; message: string; siteId: string }>(`/sites/${siteId}/swap`, {
      method: 'POST',
      body: { sessionId, selectedVersion },
    }),

  delete: (siteId: string) =>
    api<{ success: boolean }>(`/sites/${siteId}`, {
      method: 'DELETE',
    }),
};

// Enrichment
export interface EnrichmentData {
  logo?: string;
  businessName?: string;
  tagline?: string;
  description?: string;
  themeColor?: string;
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
}

export const enrich = {
  fromWebsite: (websiteUrl: string) =>
    api<{ success: boolean; data: EnrichmentData }>('/enrich', {
      method: 'POST',
      body: { websiteUrl },
    }),

  getLogo: (domain: string) =>
    api<{ success: boolean; logo: string }>(`/enrich/logo/${domain}`),
};

// Analytics
export const analytics = {
  get: (siteId: string) =>
    api<{
      totalViews: number;
      uniqueVisitors: number;
      viewsByDay: Array<{ date: string; views: number }>;
      topReferrers: Array<{ referrer: string; count: number }>;
      leadsByDay: Array<{ date: string; leads: number }>;
    }>(`/contact/${siteId}/analytics`),

  getLeads: (siteId: string, page = 1, limit = 20) =>
    api<{
      leads: Lead[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/contact/${siteId}/leads?page=${page}&limit=${limit}`),
};
